import { createContext, useContext } from 'react';

import { useBackgroundDownload } from 'lib/hooks/use-background-download';

import type {
  PackageFormInstance,
  PackageWizardContext,
  PackageWizardProviderProps,
} from './types';

/** Full value exposed by the PackageWizardCtx provider */
export interface PackageWizardCtxValue extends PackageWizardContext {
  form: PackageFormInstance;
  /** The Step 1 export and its message, kept here so they outlive the step. */
  backgroundDownload: ReturnType<typeof useBackgroundDownload>;
}

/**
 * Holds the TanStack Form instance and the mode-agnostic wizard api.
 * Steps consume `api` to upsert drafts / save packages without knowing
 * whether they're in create or edit mode.
 */
export const PackageWizardCtx = createContext<PackageWizardCtxValue | null>(
  null
);

/**
 * Provider that shares the form instance and wizard api with step
 * components via a single context.
 * @param {PackageWizardProviderProps} props - Form instance, api, children
 * @return {JSX.Element} Provider wrapping children with the wizard context
 */
export const PackageWizardProvider = ({
  form,
  api,
  children,
}: PackageWizardProviderProps) => {
  const backgroundDownload = useBackgroundDownload();
  return (
    <PackageWizardCtx.Provider value={{ form, api, backgroundDownload }}>
      {children}
    </PackageWizardCtx.Provider>
  );
};

/**
 * Hook that reads the PackageWizardCtx value. Throws if consumed outside a
 * PackageWizardProvider to catch provider-placement bugs early.
 * @return {PackageWizardCtxValue} Form instance and wizard api
 * @throws {Error} When called outside a PackageWizardProvider
 */
export const usePackageWizardCtx = (): PackageWizardCtxValue => {
  const ctx = useContext(PackageWizardCtx);
  if (ctx === null) {
    throw new Error(
      'usePackageWizardCtx must be called inside a PackageWizardProvider'
    );
  }
  return ctx;
};
